import { WorkflowStatuses } from '@opallabs/concepts'
import { ZIndex } from '@opallabs/gem'
import { kuiperClassNames, ConnectedAvatar, renderIcon } from '@opallabs/kuiper'
import { Tooltip, WorkflowIcon } from '@opallabs/lapidary'
import { track } from '@opallabs/quanta'
import ApprovalInfo from '../../../InlineApproval/ApprovalInfo'
import { getValidPeople } from '../../helpers'
import UnassignedApproval from '../../UnassignedApproval'
import ApprovalContextualMenu from '../ApprovalContextualMenu'
import { approvalActions, shouldRenderContextMenu } from '../helpers'
import './styles.scss'

type ProxyUser = {
  avatar: { url: string }
  fullName: string
  id: string
  type: 'users'
}

type ApproverControlItem = {
  to_user: {
    id: number
  }
  proxy_user: {
    id: number
  }
}

type Person = {
  id: string
  role_system_name: string
}

type CurrentUser = {
  id: string
  imageUrl: string
  name: string
}

type Approver = {
  fullName: string
  id: number
  is_active: boolean
  hasAccess: boolean
}

type Phase = { status: string }

type UpdateFunction = (params: {
  type: 'approvals'
  proxy_user?: ProxyUser
}) => void

type UpdateAndTrackFunction = (params: {
  proxy_user?: ProxyUser
  assignee?: Person
  to_user?: Person
}) => void

interface ApproverControlsProps {
  attachmentId: string | number
  comment: string
  currentUser: CurrentUser
  isActive: boolean
  isProxyApproval: boolean
  items: Array<ApproverControlItem>
  locked: boolean
  onSetProxyApprover: () => void
  people: Array<Person>
  phase: Phase
  phaseId: number
  proxyUser: Approver
  remove: () => void
  status: string
  toUser: Approver
  update: UpdateFunction
  workflowLocation: string
}

function getWorkflowStatus(status: string): WorkflowStatuses {
  switch (status) {
    case 'approved':
      return 'approval-approved'
    case 'denied':
      return 'approval-denied'
    case 'pending':
      return 'approval-pending'
    case 'initial':
      return 'approval-not-sent'
    default:
      return 'approval-not-sent'
  }
}

function getWorkflowDescription(status: string): string {
  switch (status) {
    case 'approved':
      return 'Approved'
    case 'denied':
      return 'Declined'
    case 'pending':
      return 'Approval Pending'
    case 'initial':
      return 'Approval Not Sent'
    default:
      return 'Approval Not Sent'
  }
}

const StatusIcon = ({ phase, status }: { phase: Phase; status: string }) => {
  const shouldShow = phase && phase.status !== 'new'
  const workflowStatus = getWorkflowStatus(status)
  const workflowDescription = getWorkflowDescription(status)
  return shouldShow ? (
    <Tooltip content={workflowDescription} zIndex={ZIndex.Shroud.Tooltip}>
      <div className={kuiperClassNames('status-container', 'workflow-icon')}>
        <WorkflowIcon status={workflowStatus} size={20} />
      </div>
    </Tooltip>
  ) : null
}

const NoAccess = ({ approver }: { approver: Approver }) => (
  <Tooltip
    content={`${approver.fullName} doesn't have access to this content.`}
  >
    {renderIcon('alert', {
      className: 'kuiper-no-access',
      ariaMode: 'hidden',
    })}
  </Tooltip>
)

const UserToken = ({
  approver,
  withMessage,
  workflowLocation,
}: {
  approver: Approver
  withMessage: boolean
  workflowLocation: string
}) => {
  if (!approver) {
    return null
  }

  // @TODO reenable accesss stuff once we have backend support.
  // const noAccess = (approver.hasAccess) ? null : 'no-access';
  const noAccess = null

  const disabledClass = workflowLocation === 'workflowPopover' ? 'disabled' : ''
  let fullName = approver.fullName
  if (!approver.is_active) {
    fullName = `${fullName} (Inactive)`
  }

  return (
    <div className={kuiperClassNames('user-token', noAccess, disabledClass)}>
      <div className={kuiperClassNames('message-carat')}>
        {withMessage &&
          renderIcon('triangle-lg-n', {
            style: { height: 12, width: 12 },
            ariaMode: 'hidden',
          })}
      </div>
      <ConnectedAvatar userId={approver.id} size={20} />
      {approver.hasAccess && <NoAccess approver={approver} />}
      <div className={kuiperClassNames('name-container')}>
        <div className={kuiperClassNames('full-name')}>{fullName}</div>
      </div>
    </div>
  )
}

const ApproverComponent = ({
  items,
  people,
  proxyUser,
  shouldRenderComment,
  toUser,
  updateAndTrack,
  workflowLocation,
}: {
  items: Array<ApproverControlItem>
  people: Array<Person>
  proxyUser: Approver
  shouldRenderComment: boolean
  toUser: Approver
  updateAndTrack: UpdateAndTrackFunction
  workflowLocation: string
}) => {
  if (!toUser) {
    return (
      <div className={kuiperClassNames('user-token', 'is-unassigned')}>
        <UnassignedApproval
          onSelectAssignee={(assignee: Person) =>
            updateAndTrack({ assignee, to_user: assignee })
          }
          people={getValidPeople(people, items, 'approval')}
          workflowLocation={workflowLocation}
        />
      </div>
    )
  }
  return (
    <UserToken
      approver={toUser}
      withMessage={!proxyUser && shouldRenderComment}
      workflowLocation={workflowLocation}
    />
  )
}

const ProxyApprover = ({
  proxyUser,
  shouldRenderComment,
  workflowLocation,
}: {
  proxyUser: Approver
  shouldRenderComment: boolean
  workflowLocation: string
}) => {
  if (!proxyUser) {
    return null
  }

  return (
    <div className={kuiperClassNames('transfer-user')}>
      <div className={kuiperClassNames('transfer-icon')}>
        {renderIcon('chevron-e', {
          key: 'chevron-1',
          width: 5,
          height: 8,
          ariaMode: 'hidden',
        })}
        {renderIcon('chevron-e', {
          key: 'chevron-2',
          width: 5,
          height: 8,
          ariaMode: 'hidden',
        })}
      </div>
      <UserToken
        approver={proxyUser}
        withMessage={shouldRenderComment}
        workflowLocation={workflowLocation}
      />
    </div>
  )
}

type ContextMenuProps = {
  currentUser: CurrentUser
  isActive: boolean
  items: Array<ApproverControlItem>
  locked: boolean
  onSetProxyApprover: () => void
  proxyUser: Approver
  remove: () => void
  status: string
  toUser: Approver
  updateAndTrack: UpdateAndTrackFunction
  workflowLocation: string
}

type ContextMenuAction = (typeof approvalActions)[keyof typeof approvalActions]

const ContextMenu = ({
  currentUser,
  isActive,
  items,
  locked,
  onSetProxyApprover,
  proxyUser,
  remove,
  status,
  toUser,
  updateAndTrack,
  workflowLocation,
}: ContextMenuProps) => {
  if (!shouldRenderContextMenu(isActive, workflowLocation)) {
    return null
  }
  const phase = {
    isActive,
    isLocked: locked,
    items,
    status,
  }
  const approval = {
    proxyUser,
    toUser,
  }

  const transferToCurrentUser = () => {
    if (onSetProxyApprover) {
      onSetProxyApprover()
    }
    updateAndTrack({
      proxy_user: {
        avatar: { url: currentUser.imageUrl },
        fullName: currentUser.name,
        id: currentUser.id,
        type: 'users',
      },
    })
  }

  const actionMap = {
    [approvalActions.Delete]: remove,
    [approvalActions.Transfer]: transferToCurrentUser,
  }

  const onContextMenuAction = (data: { action: ContextMenuAction }) => {
    const clickedAction = data.action
    if (clickedAction && clickedAction in actionMap) {
      actionMap[clickedAction]()
    }
  }

  return (
    <ApprovalContextualMenu
      approval={approval}
      currentUser={currentUser}
      onContextMenuAction={onContextMenuAction}
      workflowLocation={workflowLocation}
      phase={phase}
    />
  )
}

const ApproverControls = ({
  attachmentId,
  comment,
  currentUser,
  isActive,
  isProxyApproval,
  items,
  locked,
  onSetProxyApprover,
  people,
  phase,
  phaseId,
  proxyUser,
  remove,
  status,
  toUser,
  update,
  workflowLocation,
}: ApproverControlsProps) => {
  const updateAndTrack: UpdateAndTrackFunction = (params) => {
    track('Approvals: Add Approver', {
      contentId: attachmentId,
      phaseId,
    })

    update({
      ...params,
      type: 'approvals',
    })
  }

  const shouldRenderComment =
    !!comment && ['approved', 'denied'].includes(status)

  return (
    <div className={kuiperClassNames('approver-controls')}>
      <div className={kuiperClassNames('approval-users')}>
        <StatusIcon phase={phase} status={status} />
        <ApproverComponent
          items={items}
          people={people}
          proxyUser={proxyUser}
          shouldRenderComment={shouldRenderComment}
          toUser={toUser}
          updateAndTrack={updateAndTrack}
          workflowLocation={workflowLocation}
        />
        {proxyUser && isProxyApproval && (
          <ProxyApprover
            proxyUser={proxyUser}
            shouldRenderComment={shouldRenderComment}
            workflowLocation={workflowLocation}
          />
        )}
        <ContextMenu
          currentUser={currentUser}
          isActive={isActive}
          items={items}
          locked={locked}
          onSetProxyApprover={onSetProxyApprover}
          proxyUser={proxyUser}
          remove={remove}
          status={status}
          toUser={toUser}
          updateAndTrack={updateAndTrack}
          workflowLocation={workflowLocation}
        />
      </div>

      {shouldRenderComment && <ApprovalInfo feedback={comment} />}
    </div>
  )
}

export default ApproverControls
